import { useScopeParams } from "@/hooks/useScopeParams";
import { IconButton } from "@/components/UI/TableToolbar/IconButton";
import { createDownloadFileName } from "./utils";
import { useMemo, useState } from "react";
import DownloadIcon from "@/assets/icons/download.svg?react";
import InfoTooltip from "../InfoTooltip";
import { useTrackButtonClick } from "@/hooks/useTrackButtonClick";
import { prepareUnifiedDataPostRequestBody } from "./Filtering/filtersApiUtil";
import { Popover, Button } from "antd";
import { CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import { useDownloadMutation } from "@/hooks/useDownloadMutation";
import { useAppNotification } from "@/hooks/useAppNotification";
import type { SortItem } from "./Sorting/types";
import type { TableMetadataType } from "@/types/tableMetadata";
import type { ColumnsType, TableState } from "@/types/tableState.types";
import {
  buildVisibleColumnsPayload,
  buildAvailableColumnsPayload,
} from "./downloadMenuUtils";

const MAX_DOWNLOAD_PAGE_SIZE = 100000;
const DOWNLOAD_PAGE = 1;

type DownloadType = "filtered" | "full_dataset";

type BuildPayloadParams = {
  type: DownloadType;
  datasetName: string;
  subjectFilter: TableState["filters"];
  fixedFilters: TableState["filters"];
  filters: TableState["filters"];
  sorting?: SortItem[];
  metadata?: TableMetadataType;
  columns?: ColumnsType[];
  columnsTitleCreator?: (column: string) => string;
};

const buildDownloadPayload = ({
  type,
  datasetName,
  subjectFilter,
  fixedFilters,
  filters,
  sorting,
  metadata,
  columns,
  columnsTitleCreator,
}: BuildPayloadParams) => {
  const basePayload = {
    dataset_type: datasetName,
    zip: true,
  };

  if (type === "filtered") {
    const visibleColumns = buildVisibleColumnsPayload(
      columns,
      columnsTitleCreator,
    );
    return {
      ...basePayload,
      ...prepareUnifiedDataPostRequestBody({
        global_filters: { ...subjectFilter, ...fixedFilters, ...filters },
        allFiltersData: metadata?.filters,
        page: DOWNLOAD_PAGE,
        pageSize: MAX_DOWNLOAD_PAGE_SIZE,
        sorting,
      }),
      ...(visibleColumns && { columns: visibleColumns }),
    };
  }

  // Full dataset: all available + hidden columns; fixed/pinned filters
  // (e.g. a tabbed dataset page's pinned tab) still apply, but the user's
  // own adjustable filters don't.
  const availableColumns = buildAvailableColumnsPayload(
    columns,
    columnsTitleCreator,
  );
  return {
    ...basePayload,
    ...prepareUnifiedDataPostRequestBody({
      global_filters: { ...subjectFilter, ...fixedFilters },
      allFiltersData: metadata?.filters,
      page: DOWNLOAD_PAGE,
      pageSize: MAX_DOWNLOAD_PAGE_SIZE,
      sorting,
    }),
    ...(availableColumns && { columns: availableColumns }),
  };
};

const buildNotificationConfig = (
  type: "success" | "error",
  downloadType?: DownloadType,
) => {
  const isSuccess = type === "success";
  const datasetType = downloadType === "filtered" ? "filtered" : "full dataset";

  const messageText = isSuccess ? "Export ready!" : "Export failed.";
  const descriptionText = isSuccess
    ? `Your ${datasetType} dataset export is ready. Please save the file from your browser.`
    : "There was an error exporting your data. Please try again.";

  return {
    title: (
      <p
        className={
          isSuccess
            ? "text-[#003832] text-base font-semibold"
            : "text-[#73134A] text-base font-semibold"
        }
      >
        {messageText}
      </p>
    ),
    description: (
      <p
        className={
          isSuccess
            ? "text-[#2B4752] text-[14px]"
            : "text-[#260618] text-[14px]"
        }
      >
        {descriptionText}
      </p>
    ),
    placement: "bottom" as const,
    className: isSuccess
      ? "insight-success-confirmation"
      : "insight-error-confirmation",
    duration: 3,
  };
};

const DownloadMenuItem = ({
  label,
  tooltipText,
  onClick,
  disabled,
}: {
  label: string;
  tooltipText: string;
  onClick: () => void;
  disabled: boolean;
}) => (
  <button
    onClick={onClick}
    className="w-full flex flex-row gap-2 py-2 px-3 items-center justify-start hover:bg-[#ECFAFF] disabled:cursor-not-allowed transition-opacity"
    disabled={disabled}
  >
    <span>{label}</span>
    <InfoTooltip title={tooltipText} fill="#556C75" />
  </button>
);

type DownloadButtonProps = {
  name: string;
  downloadName?: string;
  filters?: { [key: string]: string[] };
  fixedFilters?: { [key: string]: string[] };
  sorting?: SortItem[];
  metadata?: TableMetadataType;
  datasetName: string;
  endpointSuffix: string;
  endpointPrefix?: string;
  noFilteredDownload: boolean;
  columns?: ColumnsType[];
  columnsTitleCreator?: (column: string) => string;
};

const DownloadButton = ({
  name,
  downloadName,
  filters = {},
  fixedFilters = {},
  sorting,
  metadata,
  datasetName,
  endpointSuffix,
  endpointPrefix,
  noFilteredDownload,
  columns,
  columnsTitleCreator,
}: DownloadButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { openSuccessNotification, openErrorNotification, contextHolder } =
    useAppNotification();
  const { scope, id, subjectId } = useScopeParams();
  const trackButtonClick = useTrackButtonClick();

  const downloadUrl = [
    endpointPrefix || `data/${scope}`,
    ...(id ? [id] : []),
    endpointSuffix,
  ]
    .filter(Boolean)
    .join("/");

  const downloadFileName = useMemo(() => {
    const baseName = createDownloadFileName(downloadName || name);
    return subjectId ? `${baseName}_${subjectId}` : baseName;
  }, [downloadName, name, subjectId]);

  const subjectFilter: { [key: string]: string[] } = subjectId
    ? { subject: [subjectId] }
    : {};

  const { download, isDownloading } = useDownloadMutation({
    onSuccess: (variables) => {
      const downloadType = variables.metadata?.downloadType as DownloadType;
      openSuccessNotification(buildNotificationConfig("success", downloadType));
    },
    onError: () => {
      openErrorNotification(buildNotificationConfig("error"));
    },
  });

  const handleDownload = (type: DownloadType) => {
    const trackingLabel =
      type === "filtered" ? "Export configured view" : "Export full view";
    trackButtonClick(trackingLabel, {
      downloadType: type,
      tableName: name,
    });

    const payload = buildDownloadPayload({
      type,
      datasetName,
      subjectFilter,
      fixedFilters,
      filters,
      sorting,
      metadata,
      columns,
      columnsTitleCreator,
    });

    setIsOpen(false);

    download({
      downloadUrl,
      fileName: `${downloadFileName}_${type}_dataset`,
      payload,
      metadata: { downloadType: type },
    });
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!isDownloading) {
      setIsOpen(newOpen);
    }
  };

  return (
    <>
      {contextHolder}
      <Popover
        content={
          <div
            className="flex flex-col items-start justify-start w-full py-2"
            style={{ minWidth: "200px" }}
          >
            {!noFilteredDownload && (
              <DownloadMenuItem
                label="Export filtered view"
                tooltipText="Exports a zip file of data with selected filters applied."
                onClick={() => handleDownload("filtered")}
                disabled={isDownloading}
              />
            )}

            <DownloadMenuItem
              label="Export full view"
              tooltipText="Exports a zip file of all available and hidden fields, excluding filters."
              onClick={() => handleDownload("full_dataset")}
              disabled={isDownloading}
            />
          </div>
        }
        title={
          <div className="flex items-center justify-between pb-1 border-b">
            <p className="text-sm font-semibold">Downloads</p>
            <Button
              type="text"
              onClick={() => setIsOpen(false)}
              icon={<CloseOutlined className="text-[#00000073]" />}
              disabled={isDownloading}
            />
          </div>
        }
        trigger="click"
        placement="bottomRight"
        arrow={false}
        open={isOpen}
        onOpenChange={handleOpenChange}
        autoAdjustOverflow={false}
        align={{ offset: [0, 10] }}
        destroyOnHidden
      >
        <span>
          <IconButton
            icon={
              isDownloading ? (
                <LoadingOutlined spin style={{ fontSize: "20px" }} />
              ) : (
                <DownloadIcon width={24} height={24} />
              )
            }
            title={
              isDownloading
                ? "Download in progress..."
                : "Export filtered view or full dataset as CSV"
            }
            onClick={() => !isDownloading && setIsOpen(!isOpen)}
            isOpen={isOpen}
            disabled={isDownloading}
            data-testid="table-toolbar-export-button"
          />
        </span>
      </Popover>
    </>
  );
};

export default DownloadButton;
